<template>
    <div>
        <DCard
            padding
        >
            <div class="mb-5">
                <h4>
                    {{ __('tools.emails.editor.create') }} "{{ __(`tools.emails.email.${removeSuffixFromString(template.name)}.name`) }}"
                </h4>
                <span class="text-muted fst-italic">
                    {{ __(`tools.emails.email.${removeSuffixFromString(template.name)}.description`) }}
                </span>
            </div>
            <DForm @submit.prevent>
                <div class="code-textarea-container">
                    <DRow>
                        <DCol col="7">
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.brand')"
                                is-required
                                :error="validationErrors['brand']"
                            >
                                <DSelect
                                    v-model="form.brand"
                                    :options="brandOptions"
                                    @update:value="updateSelectedBrand"
                                />
                            </DFormRow>
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.language')"
                                is-required
                                :error="validationErrors['language_code']"
                            >
                                <DSelect
                                    :options="languages"
                                    :model-value="form.language_code"
                                    @update:value="handleLanguageChange"
                                />
                            </DFormRow>
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.subject')"
                                is-required
                                :error="validationErrors['template.subject']"
                            >
                                <DFormTextarea
                                    id="subject"
                                    v-model="form.subject"
                                    rows="5"
                                />
                            </DFormRow>
                            <PreviewText
                                :content="form.subject"
                                :email-type="removeSuffixFromString(template.name)"
                                :sample="sample"
                            />
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.preheader_text')"
                                is-required
                                :error="validationErrors['template.preheader_text']"
                            >
                                <DFormTextarea
                                    id="preheader_text"
                                    v-model="form.preheader_text"
                                    rows="5"
                                />
                            </DFormRow>
                            <PreviewText
                                :content="form.preheader_text"
                                :email-type="removeSuffixFromString(template.name)"
                                :sample="sample"
                            />
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.content')"
                                is-required
                                :error="validationErrors['template.content']"
                            >
                                <DFormTextarea
                                    id="content"
                                    v-model="form.content"
                                    rows="15"
                                />
                            </DFormRow>
                            <PreviewText
                                :content="form.content"
                                :email-type="removeSuffixFromString(template.name)"
                                :sample="sample"
                            />
                            <DFormRow
                                class="mb-4"
                                is-vertical
                                :label="__('tools.emails.editor.links')"
                                :error="validationErrors['links']"
                            >
                                <DSelect
                                    v-model="form.links"
                                    :options="links"
                                    :model-value="selectedLink"
                                    @update:modelValue="updateSelectedLink"
                                />
                            </DFormRow>
                        </DCol>
                        <DCol col="5">
                            <h2 class="h5">
                                {{ __('tools.emails.footer_legal_info.legend') }}
                            </h2>
                            <EmailPlaceholders :placeholders="placeholders" />
                        </DCol>
                    </DRow>
                </div>
            </DForm>
            <DFabs>
                <DFab
                    id="btn-save"
                    icon="eye"
                    :label="__('messages.preview')"
                    @click="handlePreview"
                />
            </DFabs>
            <EmailEditPreviewModal
                :show-modal="showModal"
                :brands="brands"
                :email-type="sample"
                :mailables="getMailables"
                :preview-route="previewRoute"
                :content-update-route="contentUpdateRoute"
                :form="form"
                @closeModal="closeModal"
                @modalError="handleModalError"
            />
        </DCard>
    </div>
</template>

<script>

import PreviewText from '~@/Components/Tools/Emails/PreviewText'
import EmailPlaceholders from '~@/Components/Tools/Emails/Placeholders'
import EmailEditPreviewModal from '~@/Components/Tools/Emails/EmailEditPreviewModal'

export default {
    name: 'EmailEditor',
    components: { EmailPlaceholders, PreviewText, EmailEditPreviewModal },
    props: {
        placeholders: {
            type: Object,
            default: () => { return {} }
        },
        template: {
            type: Object,
            default: () => { return {} }
        },
        allTemplates: {
            type: Object,
            default: () => { return {} }
        },
        errors: {
            type: Object,
            default: () => { return {} }
        },
        brands: {
            type: Array,
            default: () => { return [] }
        },
        samples: {
            type: Array,
            default: () => { return [] }
        },
        links: {
            type: Array,
            default: () => { return [] }
        },
        availableLanguages: {
            type: Array,
            default: () => { return [] }
        },
        allLanguages: {
            type: Array,
            default: () => { return [] }
        }
    },
    data () {
        return {
            previewContent: null,
            sample: this.samples[0] ?? null,
            form: this.$inertia.form({
                brand: null,
                subject: this.template.subject,
                content: this.template.content,
                preheader_text: this.template.preheader_text,
                emailType: this.removeSuffixFromString(this.template.name),
                links: this.template.links,
                language_code: this.brands[0]?.default_customer_language
            }),
            validationErrors: this.errors,
            showModal: false,
            previewRoute: this.$route('tools.emails.editor.preview'),
            contentUpdateRoute: this.$route('tools.emails.editor.store', { mailTemplate: this.template.name }),
            selectedLink: this.template.links,
            selectedLanguage: null,
            languages: []
        }
    },
    computed: {
        getMailables () {
            return [
                {
                    type: this.removeSuffixFromString(this.template.name),
                    samples: this.samples
                }
            ]
        },
        brandOptions  () {
            const options = this.brands.map((brand) => {
                return {
                    label: brand.name,
                    value: brand.id
                }
            })

            options.unshift({
                label: '',
                value: null,
                disabled: true,
                hidden: true,
                selected: true
            })

            return options
        }
    },
    mounted () {
        this.languages = this.getAvailableLanguages()
        this.form.language_code = this.getAvailableLanguages()[0].value
        this.updateTemplate(this.form.language_code)
    },
    methods: {
        handlePreview () {
            this.showModal = true
            this.validationErrors = this.errors
        },
        closeModal () {
            this.showModal = false
        },
        handleModalError (errorObject) {
            Object.keys(errorObject).forEach((field) => {
                errorObject[field].forEach((message) => {
                    this.validationErrors[field] = message
                })
            })
        },
        updateSelectedLink (value) {
            this.selectedLink = value
        },
        handleLanguageChange (item) {
            this.form.language_code = item
            this.updateTemplate(item)
        },
        updateTemplate (language) {
            const mailTemplate = this.allTemplates.filter(function (item) { return item.language_code === language })

            if (mailTemplate.length === 0) {
                mailTemplate.push(this.template) // setting default template if there is no match
            }

            this.form.subject = mailTemplate[0].subject
            this.form.content = mailTemplate[0].content
            this.form.preheader_text = mailTemplate[0].preheader_text
            this.form.links = mailTemplate[0].links
        },
        getAvailableLanguages () {
            const lang = []

            if (!this.form.brand) {
                return this.allLanguages
            }

            this.availableLanguages.forEach((item) => {
                if (item.brand_id === this.form.brand) {
                    lang.push(item.languages)
                }
            })

            return lang[0]
        },
        updateSelectedBrand (item) {
            const brandId = parseInt(item)
            this.form.brand = brandId
            this.languages = this.getAvailableLanguages()
            this.form.language_code = this.brands.filter(brand => brand.id === brandId)[0].default_customer_language
            this.updateTemplate(this.form.language_code)
        },
        removeSuffixFromString (inputString) {
            const regex = /(_\d+)?__\w{2}$/

            return inputString.replace(regex, '')
        }
    }
}
</script>
